import { MaybeRefOrGetter, toValue } from "vue";
import { useEventListener } from "@vueuse/core";

export interface PinchEvent {
  scale: number;
  centerX: number;
  centerY: number;
}

/**
 * Detects two-finger pinch gestures via Pointer Events.
 *
 * Reports the scale delta (relative to the previous frame) plus the gesture
 * center in client coordinates each time both pointers move. Tested against
 * touch and pen pointer types; mouse pointers are ignored to avoid clashing
 * with the wheel-zoom path.
 */
export function usePinch(
  target: MaybeRefOrGetter<HTMLElement | null | undefined>,
  callback: (event: PinchEvent) => void,
) {
  const pointers = new Map<number, { x: number; y: number }>();
  let lastDistance: number | null = null;

  const distance = () => {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const center = () => {
    const [a, b] = [...pointers.values()];
    return { centerX: (a.x + b.x) / 2, centerY: (a.y + b.y) / 2 };
  };

  const reset = () => {
    lastDistance = null;
  };

  useEventListener(target, "pointerdown", (evt: PointerEvent) => {
    if (evt.pointerType === "mouse") return;
    pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
    if (pointers.size === 2) lastDistance = distance();
  });

  useEventListener(target, "pointermove", (evt: PointerEvent) => {
    if (!pointers.has(evt.pointerId)) return;
    pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
    if (pointers.size !== 2 || lastDistance === null) return;
    evt.preventDefault();
    const current = distance();
    if (current === 0) return;
    const scale = current / lastDistance;
    lastDistance = current;
    const { centerX, centerY } = center();
    const el = toValue(target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    callback({
      scale,
      centerX: centerX - rect.left,
      centerY: centerY - rect.top,
    });
  });

  const release = (evt: PointerEvent) => {
    if (!pointers.delete(evt.pointerId)) return;
    if (pointers.size < 2) reset();
    // A third finger lifting (3 → 2) leaves a stale lastDistance from the
    // earlier pair; re-seed it from the surviving pair so the next move
    // doesn't report a sudden scale jump.
    else if (pointers.size === 2) lastDistance = distance();
  };
  // Deliberately not listening to "pointerleave": a finger sliding outside the
  // container mid-pinch should not abort the gesture. pointerup/pointercancel
  // cover the real end-of-gesture cases.
  useEventListener(target, "pointerup", release);
  useEventListener(target, "pointercancel", release);
}
